import {clsx} from 'clsx';
import type {
  UseComboboxGetItemPropsReturnValue,
  UseComboboxGetMenuPropsReturnValue,
} from 'downshift';
import type {PropsWithChildren} from 'react';
import {memo} from 'react';

import * as styles from './style.module.scss';

type ItemProps = {
  isSelected: boolean;
  isDisabled?: boolean;
  isLastCustom?: boolean;
} & UseComboboxGetItemPropsReturnValue;

const List = memo(function List({
  children,
  ...props
}: PropsWithChildren<UseComboboxGetMenuPropsReturnValue>) {
  return (
    <ul {...props} className={styles.list}>
      {children}
    </ul>
  );
});
/**
 * Item component in an element with role=listbox.
 *
 * There seems to be some confusion about aria-selected as it applies to hover/focus states vs selected states.
 * @see https://github.com/downshift-js/downshift/issues/935#issuecomment-2124069121
 * For now leaving the default downshift behavior intact and using the `isSelected` prop for styling selected states.
 *
 * @param isSelected - Whether the item is selected.
 * @param isDisabled - Whether the item is disabled and cannot be selected.
 */
const Item = memo(function Item({
  children,
  isSelected,
  isDisabled,
  isLastCustom,
  ...props
}: PropsWithChildren<ItemProps>) {
  const classes = clsx(styles.item, {
    [styles.lastCustom]: isLastCustom,
    [styles.selected]: isSelected,
    [styles.disabled]: isDisabled,
  });

  return (
    <li {...props} className={classes}>
      {children}
    </li>
  );
});

export type {ItemProps};
export {List, Item};
